import GeolocationDBDetector from '../utils/geo-detector.js';

export class EstimateDelivery extends HTMLElement {
  constructor() {
    super();
    this.daysMin = 3;
    this.daysMax = 5;
    this.holidays = [];
    
    // Timeline properties
    this.daysOrderPlaced = 1;
    this.daysOrderProcessed = 0;
    this.daysOrderShipped = 0;
    
    this.stateRegionMap = {
      california: ["California"],
      pnw: ["Oregon", "Washington", "Idaho"],
      nevada: ["Nevada"],
      newmexico: ["New Mexico"],
      texas: ["Texas"],
      midwest: [
        "Illinois","Indiana","Iowa","Kansas","Michigan",
        "Minnesota","Missouri","North Dakota","Nebraska",
        "South Dakota","Wisconsin","Montana","Wyoming","Utah","Colorado","Oklahoma","Louisiana","Arizona","Arkansas"
      ],
      eastcoast: [
        "Alabama","Maine","New Hampshire","Vermont","Massachusetts","Rhode Island",
        "Connecticut","New York","New Jersey","Pennsylvania","Delaware",
        "Maryland","Virginia","North Carolina","South Carolina","Georgia",
        "Florida","West Virginia","Ohio","Tennessee","Kentucky","Mississippi"
      ],
      other: [
        "Alaska","Hawaii"
      ]
    };
    this.shippingRegions = {};

    this.geo = new GeolocationDBDetector();
  }

  connectedCallback() {

    [...this.attributes].forEach(attr => {
      if (attr.name.startsWith("region-")) {
        const key = attr.name.replace("region-", "");
        const [min, max] = attr.value.split(",").map(v => parseInt(v, 10));
        this.shippingRegions[key] = { min, max };
      }
    });

    this.init();
  }

  disconnectedCallback() {
    console.log("EstimateDelivery removed, cleaning up...");
  }

  async init() {

    await this.fetchHolidays();
    await this.detectUserState(); 
    
    // Initialize timeline calculations after location detection
    this.calculateTimelineDays();
    
    this.startCountdown();
    this.setShipDate();
    this.showEstimatedTimeline();
  }

  async detectUserState() {
    try {
      const location = await this.geo.obtenerUbicacion();
      if (!location) throw new Error("Location not available");

      this.userState = location.state || "Alaska";
      this.city = location.city;
      this.country = location.country;

      this.region = this.getRegionByState(this.userState);

      const days = this.shippingRegions[this.region];
      this.daysMin = days.min;
      this.daysMax = days.max;

    } catch (err) {
      const days = this.shippingRegions["other"];
      this.daysMin = days.min;
      this.daysMax = days.max;
      console.warn("Could not detect state:", err);
      this.userState = "Alaska";
      this.region = "other";
    }
  }

  //   REGIONS AND STATES
  getRegionByState(stateCode) {
    for (const region in this.stateRegionMap) {
      if (this.stateRegionMap[region].includes(stateCode)) {
        return region;
      }
    }
    console.log('State not found in any region, defaulting to "other"');
    return "other";
  }

  //   HOLIDAYS
  async fetchHolidays() {
    try {
      const year = new Date().getFullYear();
      const resp = await fetch(`https://date.nager.at/api/v3/publicholidays/${year}/US`);
      if (resp.ok) {
        const data = await resp.json();
        this.holidays = data.map(h => h.date);
      }
    } catch (e) {
      console.warn("No holidays loaded", e);
    }
  }

  isHoliday(date) {
    return this.holidays.includes(date.toISOString().split("T")[0]);
  }

  //   DELIVERY DATES
  getNextBusinessDate(startDate, daysToAdd) {
    const date = new Date(startDate);
    while (daysToAdd > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6 && !this.isHoliday(date)) {
        daysToAdd--;
      }
    }
    return date;
  }

  getDeliveryRange() {
    const now = new Date();
    const minDate = this.getNextBusinessDate(now, this.daysMin);
    const maxDate = this.getNextBusinessDate(minDate, this.daysMax - this.daysMin);
    const options = { month: "short", day: "numeric" };
    return this.daysMin === this.daysMax
      ? minDate.toLocaleDateString("en-US", options)
      : `${minDate.toLocaleDateString("en-US", options)} - ${maxDate.toLocaleDateString("en-US", options)}`;
  }


  //   TIMELINE CALCULATIONS
  calculateTimelineDays() {

    this.daysOrderPlaced = 1;
    this.daysOrderShipped = this.daysMax;
    this.daysOrderShipped = this.daysMax; 
    // orderProcessed = halfway between placed and shipped
    const diff = this.daysOrderShipped - this.daysOrderPlaced;
    this.daysOrderProcessed = this.daysOrderPlaced + Math.floor(diff / 2);
  }

  showEstimatedTimeline() {
    const now = new Date();

    const orderPlaced = this.getNextBusinessDate(now, 0);
    const orderProcessed = this.getNextBusinessDate(now, this.daysOrderProcessed);
    const orderShipped = this.getNextBusinessDate(now, this.daysOrderShipped);

    const options = { weekday: "short", month: "short", day: "numeric" };

    const placedText = orderPlaced.toLocaleDateString("en-US", options);
    const processedText = orderProcessed.toLocaleDateString("en-US", options);
    const shippedText = orderShipped.toLocaleDateString("en-US", options);

    const placedEl = this.querySelector(".js-placed");
    const processedEl = this.querySelector(".js-processed");
    const shippedEl = this.querySelector(".js-shipped");

    if (placedEl) placedEl.textContent = placedText;
    if (processedEl) processedEl.textContent = processedText;
    if (shippedEl) shippedEl.textContent = shippedText;
  }


  //   VIEW AND COUNTDOWN
  updateView() {
    const now = new Date();
    const target = new Date(now);
    target.setHours(24, 0, 0, 0);
    const diff = target - now;

    const h = Math.floor(diff / 1000 / 60 / 60);
    const m = Math.floor((diff / 1000 / 60) % 60);
    const s = Math.floor((diff / 1000) % 60);

    const hourEl = this.querySelector(".hour-count");
    const minuteEl = this.querySelector(".minute-count");
    const secondEl = this.querySelector(".second-count");

    if (hourEl) hourEl.textContent = String(h).padStart(2, "0");
    if (minuteEl) minuteEl.textContent = String(m).padStart(2, "0");
    if (secondEl) secondEl.textContent = String(s).padStart(2, "0");
  }

  setShipDate() {
    const shipDateEl = this.querySelector(".ship-date");
    if (shipDateEl) {
      shipDateEl.textContent = this.getDeliveryRange();
    }
  }

  startCountdown() {
    if (this._interval) clearInterval(this._interval);
    this.updateView();
    this._interval = setInterval(() => this.updateView(), 1000);
    this.style.display = "";
  }
}

if (!customElements.get("estimate-delivery")) {
  customElements.define("estimate-delivery", EstimateDelivery);
}